import React, { useState, useEffect, useRef } from 'react';
import { Paper, Collapse, Box, Button } from '@mui/material';
import Clock from './clock';

// Updated: Replaced toggle switch with tabs for Analog/Digital clock selection
const TimeWidget = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isExpanded, setIsExpanded] = useState(false);
  const getInitialPosition = () => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // Position at bottom-left on mobile with some margin
      return { x: 20, y: window.innerHeight - 120 };
    }
    // Desktop position at bottom-right
    return { x: window.innerWidth - 200, y: window.innerHeight - 100 };
  };
  const [position, setPosition] = useState(getInitialPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [clockType, setClockType] = useState('analog'); // 'analog' or 'digital'
  const widgetRef = useRef(null);

  // Get user's timezone
  useEffect(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Validate timezone by trying to use it
      new Intl.DateTimeFormat('en-US', { timeZone: timezone });
      setUserTimezone(timezone || 'UTC');
    } catch (error) {
      console.warn('Could not detect timezone, using UTC:', error);
      setUserTimezone('UTC');
    }
  }, []);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle window resize to reposition widget if needed
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      const maxX = window.innerWidth - (widgetRef.current?.offsetWidth || 100);
      const maxY = window.innerHeight - (widgetRef.current?.offsetHeight || 100);
      
      setPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle mouse/touch down for dragging (entire widget is draggable)
  const handleMouseDown = (e) => {
    // Support both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setIsDragging(true);
    const rect = widgetRef.current.getBoundingClientRect();
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });
  };

  // Handle mouse/touch move for dragging
  useEffect(() => {
    const handleMove = (e) => {
      if (isDragging) {
        e.preventDefault();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        const newX = clientX - dragOffset.x;
        const newY = clientY - dragOffset.y;

        // Keep widget within viewport bounds
        const maxX = window.innerWidth - (widgetRef.current?.offsetWidth || 100);
        const maxY = window.innerHeight - (widgetRef.current?.offsetHeight || 100);

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragOffset]);

  // Format time based on user's timezone
  const formatTime = (date, options) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        ...options,
        timeZone: userTimezone || 'UTC',
      }).format(date);
    } catch (error) {
      // Fallback to local time if timezone is invalid
      return new Intl.DateTimeFormat('en-US', options).format(date);
    }
  };

  const getTimeString = () => {
    return formatTime(currentTime, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getFullDateTime = () => {
    const dayName = formatTime(currentTime, { weekday: 'long' });
    const date = formatTime(currentTime, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
    const time = formatTime(currentTime, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    return { dayName, date, time };
  };

  const { dayName, date, time } = getFullDateTime();

  return (
    <Paper
      ref={widgetRef}
      elevation={8}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      sx={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
        cursor: isDragging ? 'move' : 'pointer',
        borderRadius: '12px',
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
        transition: isDragging ? 'none' : 'all 0.3s ease',
        minWidth: isExpanded ? { xs: '140px', sm: '150px' } : { xs: '70px', sm: '75px' },
        width: isExpanded ? { xs: '220px', sm: '250px' } : { xs: '90px', sm: '100px' },
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #06b6d4 100%)',
        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.5), 0 0 20px rgba(6, 182, 212, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        '&:hover': {
          boxShadow: '0 12px 32px rgba(59, 130, 246, 0.6), 0 0 30px rgba(6, 182, 212, 0.4)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box
        sx={{
          padding: '6px 8px',
          color: 'white',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
        }}
      >
        {/* Time Display */}
        <Box
          onClick={(e) => {
            // Prevent button clicks from collapsing
            if (e.target.closest('.MuiButton-root')) return;
            setIsExpanded(!isExpanded);
          }}
          sx={{
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              fontSize: isExpanded ? '16px' : '13px',
              fontWeight: 'bold',
              fontFamily: 'monospace',
              letterSpacing: '0.3px',
              transition: 'all 0.3s ease',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              color: '#ffffff',
            }}
          >
            {getTimeString()}
          </Box>

          {/* Expanded Details */}
          <Collapse in={isExpanded}>
            <Box
              sx={{
                marginTop: '6px',
                paddingTop: '6px',
                borderTop: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              {/* Two Separate Buttons for Analog/Digital */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', mb: 2 }}>
                <Button
                  variant={clockType === 'analog' ? 'contained' : 'outlined'}
                  onClick={() => setClockType('analog')}
                  sx={{
                    color: clockType === 'analog' ? '#1e3a8a' : '#fff',
                    backgroundColor: clockType === 'analog' ? '#fff !important' : 'transparent',
                    borderColor: '#fff',
                    minWidth: '80px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: clockType === 'analog' ? 'rgba(255, 255, 255, 0.9) !important' : 'rgba(255, 255, 255, 0.1)',
                      borderColor: '#fff',
                    },
                  }}
                >
                  Analog
                </Button>
                <Button
                  variant={clockType === 'digital' ? 'contained' : 'outlined'}
                  onClick={() => setClockType('digital')}
                  sx={{
                    color: clockType === 'digital' ? '#1e3a8a' : '#fff',
                    backgroundColor: clockType === 'digital' ? '#fff !important' : 'transparent',
                    borderColor: '#fff',
                    minWidth: '80px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: clockType === 'digital' ? 'rgba(255, 255, 255, 0.9) !important' : 'rgba(255, 255, 255, 0.1)',
                      borderColor: '#fff',
                    },
                  }}
                >
                  Digital
                </Button>
              </Box>
              <Box
                sx={{
                  marginTop: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {clockType === 'analog' ? (
                  <Clock />
                ) : (
                  <Box
                    sx={{
                      fontSize: '2rem',
                      fontFamily: 'monospace',
                      color: '#fff',
                      background: 'rgba(0,0,0,0.2)',
                      borderRadius: '12px',
                      padding: '10px 18px',
                      minWidth: '180px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 2 }}>
                      {dayName}
                    </div>
                    <div style={{ fontSize: '0.95rem', marginBottom: 4 }}>
                      {date}
                    </div>
                    <div>
                      {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Paper>
  );
};

export default TimeWidget;
